import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDate } from 'app/helpers/formatDate';
import { sortCompare } from 'app/helpers/sortCompare';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListJiraIssuesReq } from 'app/interfaces/requests/Exposed/exposedListJiraIssuesReq';
import { ListAssistTypesConfigRes } from 'app/interfaces/responses/List/listAssistTypesConfigRes';
import { ReadAssistsConfigRes } from 'app/interfaces/responses/Read/readAssistsConfigRes';
import { JiraIssuesListRes } from 'app/interfaces/support/jira/jiraIssuesListRes';
import { JiraService } from 'app/services/api/jira/jira.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-assists-jira-atvik',
    templateUrl: './assists-jira-atvik.component.html',
    styleUrls: ['./assists-jira-atvik.component.scss'],
})
export class AssistsJiraAtvikComponent implements OnInit, AfterViewInit {
    @ViewChild('sentinel', { static: false }) sentinel!: ElementRef;

    files: File[] = [];
    issues: Array<JiraIssuesListRes> = [];
    isLoading: boolean = true;
    showMyIncidents: boolean = false;
    loadSessionId: number = 0;
    // Keep track of if the initial fetch of issues is complete
    initialFetchComplete: boolean = false;

    private observer!: IntersectionObserver;
    pageSize: number = 50;
    isLastPage: boolean = false;
    nextPageToken: string = null;
    orgRequestsColumns: string[] = ['key', 'title', 'created', 'status'];
    displayedData: MatTableDataSource<JiraIssuesListRes> = new MatTableDataSource();

    listAssistTypes: Array<ListAssistTypesConfigRes>;
    assistTypes: Array<DropDownItem> = [];
    selectedAssistType: ListAssistTypesConfigRes;
    selectedAssistConfig: ReadAssistsConfigRes;

    constructor(
        public jiraService: JiraService,
        public loaderService: LoaderService,
        public lsrauthService: LsrauthService,
        public dialog: MatDialog,
        public alertsService: AlertsService,
        public router: Router,
        public route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.showMyIncidents = this.router.url.includes('/min-atvik');

        if (this.showMyIncidents) {
            this.fetchIssues();
        }
    }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                if (this.nextPageToken && !this.isLoading) {
                    this.observer.unobserve(entry.target);
                    this.fetchIssues();
                } else if (this.isLastPage) {
                    this.observer.disconnect();
                }
            });
        });
    }

    resetObserver() {
        if (!this.observer || !this.sentinel?.nativeElement) return;
        this.observer.disconnect();
        this.observer.observe(this.sentinel.nativeElement);
    }

    private mergeByKey(existing: any[], incoming: any[]) {
        const seen = new Set(existing.map((i) => i.key));
        const merged = [...existing];
        for (const item of incoming) {
            if (!seen.has(item.key)) {
                merged.push(item);
                seen.add(item.key);
            }
        }
        return merged;
    }

    // Map helper to prevent duplicating the mapping code
    private mapIssues(res: any) {
        return (res.issues ?? []).map((issue) => ({
            created: issue.fields.created,
            key: issue.key,
            url: 'https://lsr.atlassian.net/servicedesk/customer/portal/3/' + issue.key,
            title: issue.fields.summary,
            reporterName: issue.fields.reporter.displayName,
            status: issue.fields.status.name,
            eaDate: null,
            ownerName: null,
            implementerName: null,
            assistAmount: null,
            assistAllocationDate: null,
        }));
    }

    formatDate(date) {
        return formatDate(date);
    }

    // Empty the list and reset pagination
    resetList() {
        this.issues = [];
        this.nextPageToken = null;
        this.isLastPage = false;
        this.displayedData.data = this.issues;
        this.initialFetchComplete = false;
    }

    fetchIssues() {
        this.isLoading = true;
        const currentSession = this.loadSessionId;
        const req: ExposedListJiraIssuesReq = {
            NextPageToken: this.nextPageToken,
            PageSize: this.pageSize,
            Project: 'ATVIK',
            Ssn: null,
        };

        this.jiraService.listIssues(req).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (currentSession !== this.loadSessionId) return;

                const mapped = this.mapIssues(res);

                this.issues = this.mergeByKey(this.issues, mapped);

                this.displayedData.data = this.issues;
                this.isLastPage = res.isLast;
                this.nextPageToken = res.nextPageToken ?? null;
                this.initialFetchComplete = true;
                this.resetObserver();
            },
            error: () => {
                this.isLoading = false;

                this.alertsService.changealertHeader('Villa við að ná í lista');
                this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                this.alertsService.changeAlertErrorShow(true);
            },
        });
    }

    //#region Table Sort and Filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.displayedData.filter = filterValue;
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'key':
                    return sortCompare(a.key, b.key, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'created':
                    return sortCompare(a.created, b.created, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                default:
                    return 0;
            }
        });
    }

    //#endregion

    redirectToJiraForm() {
        window.open(environment.jiraIncidentUrl, '_blank');
    }
    
    goToMyIncidents() {
        this.router.navigate(['min-atvik'], { relativeTo: this.route });
    }

    goToIncidentOverview() {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    goToAssistsOverview() {
        this.router.navigate(['../yfirlit'], { relativeTo: this.route });
    }
}
